import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { financialReports, type FinReportLine } from '@/api/financialReports';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';

type FormCode = '1' | '2' | '3' | '4';

const FORM_LABELS: Record<FormCode, string> = {
  '1': 'Ф1 — Баланс',
  '2': 'Ф2 — Звіт про фінрезультати',
  '3': 'Ф3 — Рух грошових коштів',
  '4': 'Ф4 — Власний капітал',
};

const fmt = (v: string | number) => {
  const n = Number(v);
  if (n === 0) return '—';
  return n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Спец-значення для селекту: "всі юрособи tenant'а" (наскрізний P&L).
const TENANT_SCOPE = '__tenant__';

export default function FinancialReportsPage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const [form, setForm] = useState<FormCode>('1');
  const today = new Date();
  const [periodStart, setPeriodStart] = useState(
    new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10),
  );
  const [periodEnd, setPeriodEnd] = useState(
    today.toISOString().slice(0, 10),
  );

  // Регламентовані форми (Ф1 Баланс, Ф4 Власний капітал) подаються в ДПС
  // окремо по кожній юрособі — режим "весь tenant" їм недоступний.
  const allowTenantScope = form === '2' || form === '3';

  useEffect(() => {
    if (!orgId && orgList.data?.items[0]) setOrgId(orgList.data.items[0].id);
  }, [orgList.data, orgId]);

  // Якщо переключили на регламентну форму, а вибраний tenant-scope — повертаємо
  // на першу юрособу.
  useEffect(() => {
    if (!allowTenantScope && orgId === TENANT_SCOPE && orgList.data?.items[0]) {
      setOrgId(orgList.data.items[0].id);
    }
  }, [allowTenantScope, orgId, orgList.data]);

  const reportQ = useQuery({
    queryKey: ['fin-report', form, orgId, periodStart, periodEnd],
    queryFn: () => financialReports.get({
      form_code: form,
      period_start: periodStart,
      period_end: periodEnd,
      ...(orgId === TENANT_SCOPE ? {} : { organization_id: orgId }),
    }),
    enabled: !!orgId,
  });

  // Группировка по секциях
  const linesBySection: Array<{ section: string; lines: FinReportLine[] }> = [];
  for (const line of reportQ.data?.lines ?? []) {
    const sec = line.section || '';
    const last = linesBySection[linesBySection.length - 1];
    if (last && last.section === sec) {
      last.lines.push(line);
    } else {
      linesBySection.push({ section: sec, lines: [line] });
    }
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Фінансова звітність</h2>
        <button onClick={() => window.print()}>🖨 Друкувати</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
        <div className="field">
          <label>Форма</label>
          <select value={form} onChange={(e) => setForm(e.target.value as FormCode)}>
            {(Object.keys(FORM_LABELS) as FormCode[]).map((k) =>
              <option key={k} value={k}>{FORM_LABELS[k]}</option>,
            )}
          </select>
        </div>
        <div className="field">
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {allowTenantScope && (
              <option value={TENANT_SCOPE}>📊 Весь бізнес (усі юрособи)</option>
            )}
            {orgList.data?.items.map((o) =>
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>,
            )}
          </select>
        </div>
        <div className="field">
          <label>Період з</label>
          <DatePopover value={periodStart} onChange={(v) => setPeriodStart(v)} />
        </div>
        <div className="field">
          <label>Період по</label>
          <DatePopover value={periodEnd} onChange={(v) => setPeriodEnd(v)} />
        </div>
      </div>

      {reportQ.isLoading && (
        <p style={{ color: 'var(--muted)' }}>Розрахунок…</p>
      )}

      {reportQ.data && (
        <>
          <h3 style={{ marginTop: 0 }}>{reportQ.data.form_name}</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            {reportQ.data.scope === 'tenant'
              ? `По всьому бізнесу (${reportQ.data.organization_ids.length} юр.осіб) · `
              : reportQ.data.scope === 'organizations'
                ? `По обраних юрособах (${reportQ.data.organization_ids.length}) · `
                : ''}
            Період: {new Date(reportQ.data.period_start).toLocaleDateString('uk-UA')}
            {' — '}
            {new Date(reportQ.data.period_end).toLocaleDateString('uk-UA')}
          </p>

          {linesBySection.map((sect, si) => (
            <div key={si} style={{ marginTop: '1rem' }}>
              {sect.section && (
                <h4 style={{
                  margin: '0 0 0.3rem', fontSize: '0.9rem',
                  color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)',
                  paddingBottom: '0.3rem',
                }}>
                  {sect.section}
                </h4>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-soft-2)' }}>
                    <th style={{ padding: '0.4rem', width: 70, textAlign: 'left' }}>Код</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Стаття</th>
                    <th style={{ padding: '0.4rem', width: 150, textAlign: 'right' }}>
                      На початок
                    </th>
                    <th style={{ padding: '0.4rem', width: 150, textAlign: 'right' }}>
                      На кінець
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sect.lines.map((l) => (
                    <tr key={l.line_code} style={{
                      borderTop: '1px solid var(--border-soft)',
                      background: l.is_total ? 'var(--primary-soft)' : 'transparent',
                      fontWeight: l.is_total ? 700 : 400,
                    }}>
                      <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>
                        {l.line_code}
                      </td>
                      <td style={{
                        padding: '0.4rem',
                        paddingLeft: l.parent_line ? '1.5rem' : '0.4rem',
                        color: l.is_negative ? 'var(--danger)' : 'inherit',
                      }}>
                        {l.is_negative ? '(' : ''}{l.line_name}{l.is_negative ? ')' : ''}
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(l.column_start)}
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(l.column_end)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}

      <style>{`@media print { .app__header, .app__sidebar, .toolbar button { display: none !important; } }`}</style>
    </div>
  );
}
